import React, { useEffect, useRef, useState } from "react";
import { TbReceipt } from "react-icons/tb";
import { FaInfo, FaInfoCircle, FaPlane } from "react-icons/fa";
import { FiInfo, FiX } from "react-icons/fi";
import { Button, CloseButton, DatePicker, Divider, CustomFields, Input, Portal, Select, Toast, Form, AnimatedTick } from "../../../../../../Components";
import { StampTo24hr, StampToDate, minutesToHrMin } from "../../../../../../Utils/utils";
import ExpenseAttachments from "./attachments";
import BookingSelect from "./booking-select";
import { apis, get, post } from "../../../../../../Utils/axios";
import useFetch from "../../../../../../Hooks/useFetch";
import moment from "moment";
import { useClientSettings } from "../../../../../../Hooks/useClientSettings";
import { FiPlus } from "react-icons/fi";

function AddReceipt({ onReload = () => {} }) {
	const { isEnabled } = useClientSettings();

	const [Show, setShow] = useState(false);
	const [OCRData, setOCRData] = useState();
	const [RunningOcr, setRunningOcr] = useState(false);
	const [Attachments, setAttachments] = useState([]);
	const [Loading, setLoading] = useState(false);
	console.log(Attachments);

	const initial = {
		merchant: "",
		amount: "",
		date: "",
		description: "",
		category: {},
		linkedBooking: {},
	};

	const { response, loading } = useFetch(apis.getExpenseCategories, null, { categories: [] });
	const [Success, setSuccess] = useState(false);

	const [Values, setValues] = useState(initial);

	const GET_CUSTOM_FIELDS = useRef();

	useEffect(() => {
		if (Attachments.length === 1) {
			//Run OCR only for first receipt
			runOCR(Attachments[0]);
		}
	}, [Attachments]);

	const updateValues = (obj) => {
		setValues((prev) => ({ ...prev, ...obj }));
	};

	const runOCR = (obj) => {
		setRunningOcr(true);
		get(apis.runOcr, { fileId: obj.file.id }, (r, e) => {
			if (r) {
				setOCRData(r);
				setValues((prev) => ({ ...prev, merchant: r.vendorName, date: r.receiptDate, amount: r.total }));
				setRunningOcr(false);
			} else if (e) {
				setRunningOcr(false);
			}
		});
	};

	const onSubmit = () => {
		if (!Values.merchant) {
			return Toast.error("Please enter expense merchant");
		}
		if (!Values.amount) {
			return Toast.error("Please enter expense amount");
		}
		if (!Values.date) {
			return Toast.error("Please select expense date");
		}
		if (!Values.description) {
			return Toast.error("Please enter expense description");
		}
		if (!Attachments.length) {
			return Toast.error("Please add receipt photo");
		}

		let customFields;
		if (isEnabled("ORGANIZATION")) {
			if (!Values.category?.value) {
				return Toast.error("Please select expense category");
			}

			customFields = GET_CUSTOM_FIELDS.current();
			if (!customFields) {
				return;
			}
		}

		setLoading(true);
		post(
			apis.addReceipt,
			{
				...Values,
				categoryId: Values.category.value,
				customFields,
				attachments: Attachments.map((a) => a.file.key),
				date: moment(Values.date).format("DD-MM-YYYY"),
				flightId: Values.linkedBooking?.id || null,
			},
			(r, e) => {
				setLoading(false);
				if (r) {
					setValues(initial);
					setAttachments([]);
					setSuccess(true);
					onReload();
				} else if (e) {
					Toast.handleError(e);
				}
			}
		);
	};

	if (Success) {
		return (
			<>
				<Portal>
					<div className="absolute bg-[rgba(0,0,0,0.5)] top-0 left-0 right-0 bottom-0 z-20 flex-center fade-in">
						<div className="p-8 rounded-xl bg-canvas">
							<div className="h-20 flex-center">
								<AnimatedTick />
							</div>
							<div className="mt-4 text-center">
								<h1>Receipt created</h1>
								<div className="mt-4 text-label">
									Receipt was added successfully to drafts. <br /> Please submit the receipts to start the reimbursement process.
								</div>
								<div className="gap-2 mt-6 flex-center">
									<Button
										onClick={() => {
											setSuccess(false);
											setShow(false);
										}}
									>
										Close
									</Button>
									<Button variant="primary" onClick={() => setSuccess(false)}>
										Add More
									</Button>
								</div>
							</div>
						</div>
					</div>
				</Portal>
			</>
		);
	}

	return (
		<>
			<Button onClick={() => setShow(true)} variant="primary">
				<FiPlus className="mr-2" />
				Add Receipt
			</Button>
			{Show && (
				<Portal>
					<div className="flex overflow-hidden absolute top-0 right-0 bottom-0 left-0 z-20 w-full bg-light frosted fade-in">
						<div className="overflow-y-auto flex-grow flex-center">
							<ExpenseAttachments runningOCR={RunningOcr} attachments={Attachments} updateAttachments={setAttachments} />
						</div>
						<Form onSubmit={onSubmit} className="bg-canvas border-l flex flex-col justify-between h-full w-full max-w-[30%] min-w-min">
							<div className="overflow-y-auto overflow-x-hidden flex-1">
								<div className="sticky top-0 z-10 p-4 flex-center-between bg-canvas">
									<h3 className="gap-x-2 align-center">
										<TbReceipt />
										Add Receipt
									</h3>
									<CloseButton onClick={() => setShow(false)} size="2xl" />
								</div>
								<Divider className="!border-light" />
								<div className="p-4">
									<h4>Expense Info</h4>
									<Input value={Values.merchant} onChange={(merchant) => updateValues({ merchant })} required className="mt-4" label="Merchant" placeholder="Merchant Name" />
									<Input min={0} step="any" value={Values.amount} onChange={(amount) => updateValues({ amount: amount ? Number(amount) : "" })} required type="number" label="Transaction Amount" placeholder="Enter Amount" className="mt-4" />
									<DatePicker maxDate={moment().valueOf()} backDateSelection required date={Values.date} setDate={(date) => updateValues({ date })} label="Transaction Date" className="mt-4" />
									{isEnabled("ORGANIZATION") && (
										<Select
											loading={loading}
											options={response.categories.map((c) => ({ value: c.id, label: c.name }))}
											selected={Values.category}
											onChange={(category) => updateValues({ category })}
											required
											className="mt-4"
											btnClass="w-full"
											placeholder="Select Category"
											label="Category"
										/>
									)}
									<Input value={Values.description} onChange={(description) => updateValues({ description })} required className="mt-4" label="Description" placeholder="Explain the purpose of expense" />
									<BookingSelect selected={Values.linkedBooking} onSelect={(linkedBooking) => updateValues({ linkedBooking })} className="mt-4" />
									<p className="mt-2 text-xs text-gray-600">
										<FiInfo className="inline-block mr-1" />
										You can search by flight sector, number, or booking reference
									</p>
									{Values.linkedBooking?.id && (
										<div className="mt-4">
											<div className="flex justify-between items-center mb-2">
												<div className="text-xs text-gray-600">Linked to flight booking:</div>
												<Button onClick={() => updateValues({ linkedBooking: {} })} className="btn-xs" variant="danger">
													<FiX className="mr-1" size={12} />
													Remove
												</Button>
											</div>
											<div className="overflow-hidden min-w-min rounded-xl border shadow-sm bg-canvas">
												<div className="p-4">
													<div className="flex justify-between items-start">
														<div className="align-center">
															<img
																src={`https://cdn.travelwings.com/globalassets/images/${Values.linkedBooking.airlineCode?.toUpperCase()}.jpg`}
																alt={Values.linkedBooking.airlineCode}
																className="mr-2 w-6 h-6 rounded-full"
																onError={(e) => {
																	e.target.src =
																		"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='%23ccc' d='M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z'/%3E%3C/svg%3E";
																}}
															/>
															<div className="text-xs whitespace-nowrap text-secondary">
																{Values.linkedBooking.airlineCode}-{Values.linkedBooking.flightNumber}
															</div>
														</div>
														<div className="text-xs font-bold">{StampToDate(Values.linkedBooking.departureTime)}</div>
													</div>
													<div className="flex gap-x-4 mt-3">
														<div>
															<div className="font-bold whitespace-nowrap">{Values.linkedBooking.departureCode}</div>
															<div className="text-xs text-secondary">{StampTo24hr(Values.linkedBooking.departureTime)}</div>
														</div>
														<div className="flex-grow w-24 flex-center">
															<div className="w-full">
																<div className="align-center">
																	<div className="w-2 h-2 rounded-full border border-dark" />
																	<div className="flex-grow border-t border-dotted border-dark" />
																	<FaPlane />
																	<div className="flex-grow border-t border-dotted border-dark" />
																	<div className="w-2 h-2 rounded-full border border-dark" />
																</div>
																<div className="mt-2 text-xs text-center whitespace-nowrap">{minutesToHrMin(Values.linkedBooking.duration)}</div>
															</div>
														</div>
														<div>
															<div className="font-bold whitespace-nowrap">{Values.linkedBooking.arrivalCode}</div>
															<div className="text-xs text-secondary">{StampTo24hr(Values.linkedBooking.arrivalTime)}</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
								{Values.category?.value && (
									<div className="p-4">
										<CustomFields className="mt-4" categoryId={Values.category?.value} getCustomFields={GET_CUSTOM_FIELDS} activity="EXPENSE" />
									</div>
								)}
							</div>
							<div className="p-4">
								<Button type="submit" variant="primary" className="w-full" loading={Loading}>
									Create Expense
								</Button>
							</div>
						</Form>
					</div>
				</Portal>
			)}
		</>
	);
}

export default AddReceipt;
